import React, { ElementType, ComponentPropsWithoutRef } from 'react';
import styled from 'styled-components';

type ButtonVariant = 'primary' | 'secondary' | 'text' | 'outlined';
type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps<C extends ElementType = 'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  onClick?: () => void;
  disabled?: boolean;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
  children: React.ReactNode;
  as?: C;
  to?: string; // For React Router Link compatibility
}

type StyledButtonProps = {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth?: boolean;
};

const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '8px 16px';
      case 'large':
        return '16px 24px';
      default:
        return '12px 20px';
    }
  }};
  font-size: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '14px';
      case 'large':
        return '18px';
      default:
        return '16px';
    }
  }};
  font-weight: 500;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  text-decoration: none;
  
  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'secondary':
        return `
          background-color: transparent;
          color: ${theme.colors.text.primary};
          border: 1px solid ${theme.colors.text.primary};
        `;
      case 'text':
        return `
          background-color: transparent;
          color: ${theme.colors.primary};
          padding-left: 8px;
          padding-right: 8px;
          &:hover {
            background-color: rgba(156, 39, 176, 0.05);
          }
        `;
      case 'outlined':
        return `
          background-color: transparent;
          color: ${theme.colors.primary};
          border: 1px solid ${theme.colors.primary};
        `;
      case 'primary':
      default:
        return `
          background-color: ${theme.colors.primary};
          color: #fff;
        `;
    }
  }}

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

function Button<C extends ElementType = 'button'>({ 
  variant = 'primary',
  size = 'medium',
  onClick,
  disabled = false,
  fullWidth = false,
  type = 'button',
  children,
  as,
  ...rest
}: ButtonProps<C> & Omit<ComponentPropsWithoutRef<C>, keyof ButtonProps<C>>) {
  return (
    <StyledButton
      as={as as any}
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      onClick={onClick}
      disabled={disabled}
      type={type}
      {...rest}
    >
      {children}
    </StyledButton>
  );
};

export default Button;
